'use client';

import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { Notification } from '@/db/schema/user-data';
import { Icon } from '@/components/ui/icon';
import { IconButton } from '@/components/ui/icon-button';
import { NotificationItem } from '@/components/ui/notification-item';
import { Popover } from '@/components/ui/popover';
import { ProgressBar, ProgressRing, jobProgressValue } from '@/components/ui/progress';
import {
  dismissNotificationAction,
  markNotificationReadAction,
  markAllNotificationsReadAction,
} from '@/lib/actions/notifications';
import { formatRelativeTime } from '@/lib/utils/format-relative-time';
import { pollBackoffDelay } from '@/lib/utils/poll-backoff';
import { nextPollChainId, pollDiagnosticParams } from '@/lib/utils/poll-diagnostics';
import { safeInternalPath } from '@/lib/utils/safe-internal-path';
import styles from './notification-bell.module.css';

interface ActiveJob {
  id: string;
  jobType: string;
  status: 'pending' | 'queued' | 'processing';
  currentStage: string | null;
  recipeId: string | null;
  recipeTitle: string | null;
  // inputData is opaque here — the bell only renders summary text.
  // The /generating page reads it for the full "what you asked for" panel.
  inputData: Record<string, unknown>;
  // Date when this row came straight from the server render; an ISO string
  // once the same shape has been through the poll endpoint's JSON. Only ever
  // read via `new Date(...)`, which accepts both.
  createdAt: string | Date;
  startedAt: string | Date | null;
  stageIndex: number;
  stageCount: number;
  stageLabel: string | null;
  /** Key into the `generating.stages` catalogue — localised client-side. */
  stageLabelKey: string | null;
}

interface PollPayload {
  unreadCount: number;
  recentNotifications: Notification[];
  activeJobs: ActiveJob[];
}

interface Props {
  initialUnreadCount: number;
  initialRecentNotifications: Notification[];
  /**
   * The server could not load notifications — auth unreachable, DB error. NOT
   * the same as having none, and must not render as an empty state: during the
   * #660 incident the bell cheerfully reported "No notifications yet" while 62
   * of them sat in the table, which is what made the fault invisible.
   */
  initialUnavailable?: boolean;
  /**
   * Server-rendered in-flight jobs. Because the bell no longer polls when idle,
   * without this a navigation away from /cookbook/generating left it with no
   * ring and no reason to look for one: `activeJobs` only ever arrived via a
   * poll, and the poll only continues while `activeJobs` is non-empty (#674).
   * Every authenticated render now answers it up front, which also means every
   * queue path is covered without each one remembering to dispatch the wake
   * event.
   *
   * The ROWS, not a boolean (#685). The progress ring is derived from this
   * list, so seeding it empty made every mount paint the bell icon and only
   * become a ring once a poll came back — visible as a flicker whenever the
   * boundary re-rendered, which `router.refresh()` does the moment a job ends.
   */
  initialActiveJobs?: ActiveJob[];
  /**
   * "I don't actually know" — set by the streaming fallback and the degraded
   * catch path, whose empty props mean "no data yet", not "no data" (#754).
   * When set, the bell seeds from this tab's last-known snapshot instead of
   * the passed (empty) initials, so a router.refresh() streaming the header
   * through the Suspense fallback no longer blinks the ring and badge out —
   * the residual flicker that #685 didn't cover. An authoritative render
   * (rows from the real slot) never sets this, so real emptiness still wins
   * the moment it arrives.
   */
  seedFromLastKnown?: boolean;
}

// ── Last-known bell state, per tab ──────────────────────────────────────────
//
// CLIENT-bundle module scope: one copy per browser tab, torn down with it.
// The CLAUDE.md §20 rule against per-user module state targets SERVER modules
// under Fluid Compute (shared across concurrent users); this is the client
// equivalent of a sessionStorage cache, scoped to one user's one tab by
// construction. Written on every authoritative render and successful poll;
// read only when `seedFromLastKnown` says the passed props are placeholders.
interface LastKnownBellState {
  unreadCount: number;
  notifications: Notification[];
  activeJobs: ActiveJob[];
  at: number;
}
let lastKnownBellState: LastKnownBellState | null = null;
// Past this age a snapshot is more likely to mislead than to bridge a
// streaming gap — matches ACTIVE_JOB_STALE_MS, past which the poll loop
// considers a job abandoned anyway.
const LAST_KNOWN_MAX_AGE_MS = 10 * 60_000;

// ── Poll cadence ────────────────────────────────────────────────────────────
//
// This loop was, by a wide margin, the busiest thing in the app. Vercel's
// function log for a SIX-HOUR window on a two-user household:
//
//     /api/notifications/poll ... 1,800     ← 77% of all invocations
//     /recipes/[slug] ...........   136
//     /recipes ..................    92
//     everything else ...........  ~290
//
// One invocation every twelve seconds, forever, because the old loop polled at
// 30s idle for as long as the tab was open — and ~20% of those were cold starts
// that opened a brand-new database pool. That is where the concurrent instances
// came from, and therefore where the pooler exhaustion came from (#660, #669).
// It was never /recipes; that page is a rounding error next to this.
//
// So: DO NOT POLL WHEN THERE IS NOTHING TO POLL FOR. An idle bell schedules no
// timer at all. It wakes on exactly three things:
//
//   - POLL_NOW_EVENT, dispatched by whatever just queued a job
//   - the tab becoming visible again
//   - a page navigation, which re-renders the header server-side anyway
//
// The cost is that a job queued on ANOTHER device won't appear in this tab
// until one of those happens. For a two-person household that is a fine trade
// for eliminating ~1,800 invocations per six hours.
/** Stable identity so the default prop doesn't look like new data each render. */
const EMPTY_ACTIVE_JOBS: ActiveJob[] = [];

const POLL_INTERVAL_ACTIVE_MS = 8_000;
const POLL_INTERVAL_SLOW_MS = 20_000;
const ACTIVE_JOB_FRESH_MS = 2 * 60_000;
const ACTIVE_JOB_STALE_MS = 10 * 60_000;

/**
 * Delay until the next poll, or NULL to stop polling entirely.
 *
 * Driven by the NEWEST job's age — a fresh job alongside a stale one still
 * deserves a fast cadence — and derived on each tick rather than tracked in
 * state, so it self-corrects.
 */
function pollDelayFor(jobs: ActiveJob[]): number | null {
  if (jobs.length === 0) return null;
  const now = Date.now();
  const youngestAge = Math.min(
    ...jobs.map((j) => {
      const created = new Date(j.createdAt).getTime();
      return Number.isNaN(created) ? Infinity : now - created;
    }),
  );
  if (youngestAge < ACTIVE_JOB_FRESH_MS) return POLL_INTERVAL_ACTIVE_MS;
  if (youngestAge < ACTIVE_JOB_STALE_MS) return POLL_INTERVAL_SLOW_MS;
  // Every active job is old enough that it is far more likely wedged than
  // working. Stop; the hourly watchdog will reap it, and the wake event will
  // bring us back if something real happens.
  return null;
}

/**
 * Stretch the poll cadence after consecutive failures, with jitter (#711).
 * The shape lives in `pollBackoffDelay` (src/lib/utils/poll-backoff.ts) and is
 * shared by every poll loop in the app (#754) — this loop pioneered it, and the
 * loops written without it are the ones that drove the EMAXCONN spiral.
 *
 * Backing off does NOT extend how long the loop runs — `pollDelayFor` still
 * ends it once every in-flight job is older than ACTIVE_JOB_STALE_MS, so the
 * loop stays bounded (CLAUDE.md §20). It only makes a loop that is currently
 * failing cost less while it fails.
 */
const backOff = pollBackoffDelay;

// ── Per-tab poll gate (#843) ────────────────────────────────────────────────
//
// Everything below is module scope ON PURPOSE: it must survive remount, and a
// tab may have more than one bell alive at a time.
//
// `HeaderBellFallback` renders a second full NotificationBell, and passes
// `seedFromLastKnown`, so when the seed carries active jobs the fallback polls
// too — deliberately, since a bridged bell must CONFIRM those jobs rather than
// display them statically (#754). Combined with router.refresh() swapping the
// Suspense boundary, a burst produces a stream of fresh mounts, and every mount
// used to bring a brand-new chain with `consecutiveFailures` back at 0. That is
// why the 2026-08-17 storm held a flat ~170 req/s for 30s with no decay:
// correctly-backing-off chains cannot do that, but continuously-replaced ones
// can. Per-mount state cannot fix a per-mount problem.
//
// Ownership handoff was the other candidate and is worse: if the owner unmounts
// while a non-owner bell is already mounted, nobody re-arms and the bell goes
// silent for good. A shared floor has no such failure mode — every chain still
// runs, they just cannot collectively outpace the floor.

/** Earliest wall-clock time any chain in this tab may fetch again. */
let nextPollAllowedAt = 0;
/** True while a fetch is in flight anywhere in this tab. */
let pollInFlight = false;
/**
 * Consecutive failures across the whole tab. At module scope so a remount
 * cannot reset the backoff — resetting it on mount is what removed the decay.
 */
let pollConsecutiveFailures = 0;

/**
 * Hard floor between fetches, whatever the cadence or the chain count. The
 * fastest legitimate cadence is 8s; this only bites when several chains (or a
 * wake event landing on top of a scheduled tick) would otherwise stack. Worst
 * case becomes 0.5 req/s per tab.
 */
const MIN_POLL_SPACING_MS = 2_000;

/**
 * Circuit breaker. After this many consecutive failures the tab stops polling
 * entirely rather than continuing at the 120s ceiling — across 24 server
 * instances even a slow poll is load on the resource that is already out.
 * Reset by a visibility change (the user came back) or by any success.
 */
const POLL_FAILURE_LIMIT = 8;

/** Custom DOM event other client code can dispatch on `window` to nudge the
 *  bell into polling immediately — used so the navbar progress ring appears
 *  the moment a job is queued, instead of waiting for the next idle tick. */
export const POLL_NOW_EVENT = 'cheffo:notification-bell-poll-now';

/** Convenience wrapper — dispatches the wake event on the window so the
 *  navbar bell polls right away. Safe to call on the server (no-ops). */
export function wakeNotificationBell(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(POLL_NOW_EVENT));
}

type JobTypeKey =
  | 'generate_recipe'
  | 'derive_variant'
  | 'import_url'
  | 'import_text'
  | 'import_photo'
  | 'import_pdf'
  | 'generate_image'
  | 'research_recipe'
  | 'default';

type NotificationsTranslator = ReturnType<typeof useTranslations<'notifications'>>;

function jobTypeLabel(jobType: string, t: NotificationsTranslator): string {
  const key: JobTypeKey =
    jobType === 'generate_recipe' ||
    jobType === 'derive_variant' ||
    jobType === 'import_url' ||
    jobType === 'import_text' ||
    jobType === 'import_photo' ||
    jobType === 'import_pdf' ||
    jobType === 'generate_image' ||
    jobType === 'research_recipe'
      ? jobType
      : 'default';
  return t(`jobTypes.${key}`);
}

function jobDisplayTitle(job: ActiveJob, t: NotificationsTranslator): string {
  // Prefer the actual recipe title once we have one (variant derivations and
  // imports that have already created the recipe row).
  if (job.recipeTitle) return job.recipeTitle;

  // Research names the dish it is researching — the job has no recipe row to
  // borrow a title from, and never will.
  const dish = job.inputData?.dish;
  if (job.jobType === 'research_recipe' && typeof dish === 'string' && dish.trim()) {
    return dish.trim();
  }

  // For brand-new generations, fall back to the user's description if present.
  const description = job.inputData?.userDescription;
  if (typeof description === 'string' && description.trim().length > 0) {
    const trimmed = description.trim();
    return trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed;
  }
  // For URL imports we don't have a title yet — show the host as a hint.
  const url = job.inputData?.url;
  if (typeof url === 'string') {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      // fallthrough
    }
  }
  return jobTypeLabel(job.jobType, t);
}

/** A short tag rendered as a pill on each in-progress row — captures the most
 *  informative single piece of metadata for the job (target variant for
 *  derivations, anchor variant for fresh generations, source type for
 *  imports). Returns null when nothing distinctive is available. */
function jobTagLabel(
  job: ActiveJob,
  t: NotificationsTranslator,
  tVariants: ReturnType<typeof useTranslations<'recipes.variants'>>,
): string | null {
  const data = job.inputData ?? {};
  if (job.jobType === 'derive_variant') {
    const target = data.targetVariant;
    if (target === 'quick' || target === 'classic' || target === 'bougie') {
      return tVariants(target);
    }
  }
  if (job.jobType === 'generate_recipe') {
    const anchor = data.anchorVariant;
    if (anchor === 'quick' || anchor === 'classic' || anchor === 'bougie') {
      return tVariants(anchor);
    }
  }
  if (job.jobType === 'import_url') {
    const source = data.source;
    if (source === 'youtube') return t('sources.youtube');
    if (source === 'instagram') return t('sources.instagram');
    if (source === 'blog') return t('sources.blog');
  }
  if (job.jobType === 'import_text') return t('sources.pasted');
  if (job.jobType === 'import_photo') return t('sources.photo');
  if (job.jobType === 'import_pdf') return t('sources.pdf');
  return null;
}

/** Optional secondary line — only shown for variant derivations where we
 *  also have the recipe title (so the row reads as "Recipe Title" / "Bougie
 *  variant"). For other job types the tag pill carries the same info. */
function jobSecondaryLine(
  job: ActiveJob,
  t: NotificationsTranslator,
  tVariants: ReturnType<typeof useTranslations<'recipes.variants'>>,
): string | null {
  if (job.jobType === 'derive_variant' && job.recipeTitle) {
    const target = job.inputData?.targetVariant;
    if (target === 'quick' || target === 'classic' || target === 'bougie') {
      return t('variantSuffix', { variant: tVariants(target) });
    }
  }
  if (job.jobType === 'import_url' && typeof job.inputData?.url === 'string') {
    return job.inputData.url.length > 64
      ? `${job.inputData.url.slice(0, 61)}…`
      : job.inputData.url;
  }
  return null;
}

export function NotificationBell({
  initialUnreadCount,
  initialRecentNotifications,
  initialUnavailable = false,
  initialActiveJobs = EMPTY_ACTIVE_JOBS,
  seedFromLastKnown = false,
}: Props) {
  const router = useRouter();
  const t = useTranslations('notifications');
  const tStages = useTranslations('generating.stages');
  const tVariants = useTranslations('recipes.variants');
  const [isOpen, setIsOpen] = useState(false);
  // When the caller admits its props are placeholders (streaming fallback,
  // degraded read), bridge from this tab's last-known snapshot so the ring and
  // badge hold steady through the gap (#754). Captured ONCE per mount (useState
  // initializer) so everything derived from it — including the poll-loop
  // trigger below — is stable across re-renders; the authoritative render that
  // follows replaces the boundary and seeds from real rows.
  const [seed] = useState<LastKnownBellState | null>(() =>
    seedFromLastKnown &&
    lastKnownBellState !== null &&
    Date.now() - lastKnownBellState.at < LAST_KNOWN_MAX_AGE_MS
      ? lastKnownBellState
      : null,
  );
  const [notifications, setNotifications] = useState<Notification[]>(
    seed ? seed.notifications : initialRecentNotifications,
  );
  const [unreadCount, setUnreadCount] = useState(seed ? seed.unreadCount : initialUnreadCount);
  // Cleared by the first poll that succeeds — a transient auth blip should heal
  // itself without a reload.
  const [unavailable, setUnavailable] = useState(initialUnavailable);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>(
    seed ? seed.activeJobs : initialActiveJobs,
  );
  const [, startTransition] = useTransition();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Mirrors `activeJobs` so the polling loop reads the latest list without
  // re-subscribing each tick.
  const activeJobsRef = useRef<ActiveJob[]>(seed ? seed.activeJobs : initialActiveJobs);
  useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  // Re-sync from server-provided props when the parent RSC re-renders (e.g.
  // after revalidatePath('/notifications')). Polling keeps state fresh
  // between those events. We do this with the React-canonical "adjust state
  // during render" pattern (https://react.dev/learn/you-might-not-need-an-effect)
  // rather than a useEffect → setState — the effect version trips
  // react-hooks/cascading-renders and adds a needless extra render commit.
  const [prevInitialNotifications, setPrevInitialNotifications] = useState(initialRecentNotifications);
  const [prevInitialUnreadCount, setPrevInitialUnreadCount] = useState(initialUnreadCount);
  if (prevInitialNotifications !== initialRecentNotifications) {
    setPrevInitialNotifications(initialRecentNotifications);
    setNotifications(initialRecentNotifications);
  }
  if (prevInitialUnreadCount !== initialUnreadCount) {
    setPrevInitialUnreadCount(initialUnreadCount);
    setUnreadCount(initialUnreadCount);
  }
  // Same pattern for the job list. The server read is fresher than a poll that
  // may not have run for 20s, and syncing it here is what lets a completed job
  // clear the ring on the refresh rather than waiting for the next tick.
  const [prevInitialActiveJobs, setPrevInitialActiveJobs] = useState(initialActiveJobs);
  if (prevInitialActiveJobs !== initialActiveJobs) {
    setPrevInitialActiveJobs(initialActiveJobs);
    // The mirror ref is NOT written here — refs must not be touched during
    // render. The effect above syncs it from `activeJobs`, and it runs before
    // any poll tick can read it.
    setActiveJobs(initialActiveJobs);
  }

  // Boolean, not the array, so the polling effect doesn't tear down and rebuild
  // on every RSC payload — `initialActiveJobs` is a fresh array each render.
  // Includes the seeded jobs: a bell bridging a degraded read from last-known
  // state must also poll to CONFIRM those jobs, not display them statically.
  const hasInitialActiveJobs = (seed ? seed.activeJobs : initialActiveJobs).length > 0;

  // Record every authoritative server answer as this tab's last-known state.
  // A seeded mount records nothing — its data is second-hand by definition;
  // its poll successes record below instead.
  useEffect(() => {
    if (seedFromLastKnown) return;
    lastKnownBellState = {
      unreadCount: initialUnreadCount,
      notifications: initialRecentNotifications,
      activeJobs: initialActiveJobs,
      at: Date.now(),
    };
  }, [seedFromLastKnown, initialUnreadCount, initialRecentNotifications, initialActiveJobs]);

  // Polling loop. Cadence depends on whether any job is in flight; pauses when
  // the tab is hidden so we don't burn requests in a background tab.
  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    // Identity for THIS chain, carried on every request so the next flood can
    // be attributed instead of inferred (#843). A fresh id per chain is the
    // whole point: many chains under one tab id is the mount-churn signature
    // that the 2026-08-17 export could not distinguish from many tabs.
    const chainId = nextPollChainId();
    let tickSeq = 0;

    /** Re-arm this chain, honouring the tab-wide floor. */
    function reschedule() {
      if (cancelled) return;
      const delay = pollDelayFor(activeJobsRef.current);
      // null → nothing in flight. Schedule NOTHING and let the wake event or a
      // visibility change restart us. This is the change that removes ~77% of
      // the app's function invocations.
      if (delay === null) return;
      const backedOff = backOff(delay, pollConsecutiveFailures);
      const floor = nextPollAllowedAt - Date.now();
      timeoutId = setTimeout(tick, Math.max(backedOff, floor));
    }

    async function tick() {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) {
        // Don't poll a background tab at all. visibilitychange brings us back
        // the moment it matters; a timer here would just be the old always-on
        // loop wearing a different hat.
        return;
      }
      // Circuit breaker (#843). Polling on through a sustained outage at the
      // 120s ceiling is still load on the resource that ran out, multiplied by
      // every tab and every instance. Stop; visibilitychange resets it.
      if (pollConsecutiveFailures >= POLL_FAILURE_LIMIT) return;
      // Tab-wide gate. Another chain — a Suspense-fallback bell, or a wake
      // event landing on a scheduled tick — may have just fetched. Yield to it
      // and re-arm rather than stacking a second request on top.
      if (pollInFlight || Date.now() < nextPollAllowedAt) {
        reschedule();
        return;
      }
      pollInFlight = true;
      nextPollAllowedAt = Date.now() + MIN_POLL_SPACING_MS;
      try {
        tickSeq += 1;
        const res = await fetch(
          `/api/notifications/poll?${pollDiagnosticParams(chainId, tickSeq, pollConsecutiveFailures)}`,
          { cache: 'no-store' },
        );
        if (!res.ok) {
          // 401 (session gone), 429 (rate limited) and 503 (the route could not
          // reach the database — #711) all mean we cannot speak for the
          // notification list — say so rather than keep showing a stale or
          // empty one as though it were current.
          pollConsecutiveFailures += 1;
          if (!cancelled) setUnavailable(true);
        } else {
          const data: PollPayload = await res.json();
          if (cancelled) return;
          pollConsecutiveFailures = 0;
          setUnavailable(false);
          setUnreadCount(data.unreadCount);
          setNotifications(data.recentNotifications);
          // If a previously-active job has dropped out of the list, trigger an
          // RSC refresh so any page that reads job state (e.g. recipe detail
          // after variant derivation) picks up the new row.
          const previousIds = new Set(activeJobsRef.current.map((j) => j.id));
          const currentIds = new Set(data.activeJobs.map((j) => j.id));
          const completedSomething = [...previousIds].some((id) => !currentIds.has(id));
          setActiveJobs(data.activeJobs);
          activeJobsRef.current = data.activeJobs;
          // A successful poll is authoritative — record it as this tab's
          // last-known state so the next streaming fallback bridges from it.
          lastKnownBellState = {
            unreadCount: data.unreadCount,
            notifications: data.recentNotifications,
            activeJobs: data.activeJobs,
            at: Date.now(),
          };
          if (completedSomething) router.refresh();
        }
      } catch {
        // Network failure. Same reasoning as a non-ok response: we do not know
        // the list, so do not imply that we do.
        pollConsecutiveFailures += 1;
        if (!cancelled) setUnavailable(true);
      } finally {
        // Released even on the `cancelled` early-return above — leaving this
        // set would wedge every other chain in the tab behind a flag nothing
        // can clear.
        pollInFlight = false;
      }
      reschedule();
    }

    // Do NOT poll immediately on mount. The first tick used to fire while the
    // page it lives on was still rendering server-side, so a second lambda
    // opened its own database connections at the exact moment the render was
    // opening its. Both draw on one fixed 200-client pooler budget, and that
    // overlap is what an EMAXCONN burst is made of (#672).
    //
    // Chrome waits for content. Nothing here is urgent — the bell has its
    // server-rendered initial state, and any job it would report was queued by
    // this same page.
    // Nothing running per the server, and no wake event yet → never start a
    // timer. This is what turns 1,800 invocations into a handful.
    if (!hasInitialActiveJobs) {
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener(POLL_NOW_EVENT, onWake);
      return () => {
        cancelled = true;
        if (timeoutId) clearTimeout(timeoutId);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener(POLL_NOW_EVENT, onWake);
      };
    }

    if (typeof document !== 'undefined' && document.readyState !== 'complete') {
      const onLoad = () => {
        if (!cancelled) tick();
      };
      window.addEventListener('load', onLoad, { once: true });
      // Belt and braces: if `load` never fires (a hung subresource), start
      // anyway rather than leaving the bell permanently silent.
      timeoutId = setTimeout(() => {
        window.removeEventListener('load', onLoad);
        if (!cancelled) tick();
      }, 5_000);
    } else {
      tick();
    }

    function onVisibility() {
      if (!document.hidden && !cancelled) {
        // The user came back — clear the breaker so a tab parked through an
        // outage starts fresh instead of staying dark for the session. This is
        // the ONLY reset besides a successful poll: a remount must not clear it
        // (#843), which is why the counter is not per-mount.
        pollConsecutiveFailures = 0;
        if (timeoutId) clearTimeout(timeoutId);
        tick();
      }
    }
    function onWake() {
      // External signal — fired when something queues a new job (e.g. variant
      // derivation, recipe import). Cancels the pending tick and polls right
      // away so the bell flips into its progress-ring state immediately
      // instead of waiting up to POLL_INTERVAL_IDLE_MS for the next tick.
      if (cancelled) return;
      if (timeoutId) clearTimeout(timeoutId);
      tick();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(POLL_NOW_EVENT, onWake);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(POLL_NOW_EVENT, onWake);
    };
    // We deliberately exclude `hasActive` — the loop reads the latest count
    // from `activeJobsRef` to decide cadence on each tick, so re-running the
    // effect on every active-jobs change would tear down and re-create the
    // poll loop unnecessarily.
  }, [router, hasInitialActiveJobs]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  function handleMarkRead(id: string) {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    const fd = new FormData();
    fd.set('notificationId', id);
    startTransition(async () => {
      const result = await markNotificationReadAction(fd);
      if (!result.success) {
        setNotifications(initialRecentNotifications);
        setUnreadCount(initialUnreadCount);
      }
    });
  }

  function handleDismiss(id: string) {
    const target = notifications.find((n) => n.id === id);
    const wasUnread = target ? !target.isRead : false;
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));

    const fd = new FormData();
    fd.set('notificationId', id);
    startTransition(async () => {
      const result = await dismissNotificationAction(fd);
      if (!result.success) {
        setNotifications(initialRecentNotifications);
        setUnreadCount(initialUnreadCount);
      }
    });
  }

  function handleItemClick(n: Notification, e: React.MouseEvent) {
    if (!n.isRead) handleMarkRead(n.id);
    const safePath = safeInternalPath(n.linkUrl);
    if (safePath) {
      e.preventDefault();
      setIsOpen(false);
      router.push(safePath);
    }
  }

  function handleMarkAll() {
    const previouslyUnread = notifications.filter((n) => !n.isRead).map((n) => n.id);
    if (previouslyUnread.length === 0) return;

    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    startTransition(async () => {
      const result = await markAllNotificationsReadAction();
      if (!result.success) {
        setNotifications(initialRecentNotifications);
        setUnreadCount(initialUnreadCount);
      }
    });
  }

  // Pick a representative job for the navbar ring when there are several. The
  // furthest-along processing job feels right — it's the one the user is
  // closest to seeing finish.
  const representativeJob: ActiveJob | null = useMemo(() => {
    if (activeJobs.length === 0) return null;
    const processing = activeJobs.filter((j) => j.status === 'processing');
    const pool = processing.length > 0 ? processing : activeJobs;
    return pool.reduce((best, j) => {
      const bestPct = (best.stageIndex + 1) / Math.max(1, best.stageCount);
      const jPct = (j.stageIndex + 1) / Math.max(1, j.stageCount);
      return jPct > bestPct ? j : best;
    });
  }, [activeJobs]);

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);
  const showRing = representativeJob !== null;

  const triggerLabel = showRing
    ? activeJobs.length === 1
      ? t('jobInProgressNamed', { label: jobTypeLabel(activeJobs[0].jobType, t) })
      : t('jobsInProgress', { count: activeJobs.length })
    : unreadCount > 0
    ? t('titleWithCount', { count: unreadCount })
    : t('title');

  const processing = representativeJob?.status === 'processing';

  return (
    <Popover
      align="end"
      role="dialog"
      label={t('title')}
      width="var(--bell-panel-w)"
      maxHeight="var(--popover-max-h)"
      mobileSheet
      trigger={
        <IconButton
          ref={triggerRef}
          label={triggerLabel}
          data-notification-bell
          badge={showRing ? null : unreadCount > 0 ? badgeLabel : null}
        >
          {showRing && representativeJob ? (
            <ProgressRing
              // Queued work has no value yet — the ring holds its floor and
              // pulses rather than claiming progress.
              value={
                processing
                  ? jobProgressValue(representativeJob.stageIndex, representativeJob.stageCount)
                  : undefined
              }
              label={triggerLabel}
              unread={unreadCount > 0}
            />
          ) : (
            <Icon name="bell" size={18} strokeWidth={1.5} />
          )}
        </IconButton>
      }
      header={
        <div className={styles.popoverHeader}>
          <span className={styles.popoverTitle}>{t('title')}</span>
          <button
            type="button"
            className={styles.markAllButton}
            onClick={handleMarkAll}
            disabled={unreadCount === 0}
          >
            {t('markAllAsRead')}
          </button>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Link href="/notifications" className={styles.viewAll}>
            {t('viewAll')}
          </Link>
        </div>
      }
    >
      {(close) => (
        <>
          {activeJobs.length > 0 && (
            <div className={styles.activeSection}>
              <span className={styles.sectionLabel}>{t('inProgress')}</span>
              {activeJobs.map((job) => {
                const isProcessing = job.status === 'processing';
                const tag = jobTagLabel(job, t, tVariants);
                const secondary = jobSecondaryLine(job, t, tVariants);
                const stage = isProcessing
                  ? job.stageLabelKey
                    ? tStages(job.stageLabelKey)
                    : job.stageLabel ?? jobTypeLabel(job.jobType, t)
                  : t('queuedStarting');
                return (
                  <Link
                    key={job.id}
                    href={`/cookbook/generating?jobId=${job.id}`}
                    className={styles.activeItem}
                    onClick={close}
                  >
                    <span className={styles.activeTitleRow}>
                      <span className={styles.activeTitle}>{jobDisplayTitle(job, t)}</span>
                      {tag && <span className={styles.activeTag}>{tag}</span>}
                    </span>
                    {secondary && <span className={styles.activeSecondary}>{secondary}</span>}
                    <span className={styles.activeStageRow}>
                      <span className={styles.activeStage}>{stage}</span>
                      {isProcessing && (
                        <span className={styles.activePct}>
                          {Math.round(jobProgressValue(job.stageIndex, job.stageCount))}%
                        </span>
                      )}
                    </span>
                    <ProgressBar
                      value={
                        isProcessing ? jobProgressValue(job.stageIndex, job.stageCount) : undefined
                      }
                      label={stage}
                    />
                  </Link>
                );
              })}
            </div>
          )}

          {/* Unavailable is checked BEFORE empty: "we couldn't load these" and
              "you have none" are different facts, and conflating them is what
              hid the #660 outage behind a reassuring empty state. */}
          {unavailable && notifications.length === 0 ? (
            <div className={styles.empty} role="alert">
              <span className={styles.emptyMedallion} aria-hidden="true">
                <Icon name="warn" size={20} strokeWidth={1.75} />
              </span>
              <span className={styles.emptyText}>{t('unavailable')}</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyMedallion} aria-hidden="true">
                <Icon name="bell" size={20} strokeWidth={1.75} />
              </span>
              <span className={styles.emptyText}>{t('noNotifications')}</span>
            </div>
          ) : (
            <ul className={styles.list}>
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  density="compact"
                  type={n.type}
                  title={n.title}
                  body={n.body}
                  timestamp={formatRelativeTime(n.createdAt)}
                  isRead={n.isRead}
                  href={safeInternalPath(n.linkUrl)}
                  onActivate={() => {
                    if (!n.isRead) handleMarkRead(n.id);
                    close();
                  }}
                  onDismiss={() => handleDismiss(n.id)}
                  markReadLabel={t('markAsRead')}
                  dismissLabel={t('dismissNamed', { title: n.title })}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Popover>
  );
}
